import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import classNames from 'classnames';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { ListMatchingBlockSettings } from './ListMatchingBlockSettings';
import { Col, Container, Row } from 'reactstrap';
import { R } from '@core/utils/r';
import { HTMLService } from '@business/services';
import { SortableElement, SortEnd } from 'react-sortable-hoc';
import { SortableList } from '@modules/common/Sortable';
import { Button } from '@core/components/Button';
import { useBloc } from '@core/utils/bloc';
import { ListMatchingBlockBloc } from './ListMatchingBlockBloc';
import { of } from 'rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import './index.scss';
import { useDidMount } from '@core/utils/hooks/react';
import { Sortable } from '@modules/common';

export interface MatchingListItem {
  id: string;
  item: string;
  matchingItem: string;
}

function defineListMatchingBlockForm() {
  return formGroup({
    instructions: formControl<string>({
      initialValue: 'Instructions',
      label: 'Instructions',
      required: true,
    }),
    items: formArray(() => defineListMatchingItemsForm()),
    advance: defineBlockAdvanceForm(),
  });
}

function defineListMatchingItemsForm() {
  return formGroup({
    item: formControl<string>({
      initialValue: 'item',
      required: true,
    }),
    matchingItem: formControl<string>({
      initialValue: 'matching item',
      required: true,
    }),
  });
}

export type ListMatchingWidgetFormData = FormDataType<typeof defineListMatchingBlockForm>;
export type ListMatchingWidgetForm = ReturnType<typeof defineListMatchingBlockForm>;

export type ListMatchingItemForm = ReturnType<typeof defineListMatchingItemsForm>;

export enum ListMatchingBlockVariation {
  listMatching = 'listMatching',
}

export const ListMatchingBlock = memo((props: CourseBuilder.BlockProps<ListMatchingBlockVariation>) => {
  const { variation, preview, editable, lessonId } = props;
  const bloc = useBloc(ListMatchingBlockBloc, lessonId);
  const form = useMemo(defineListMatchingBlockForm, []);
  const formValue = useNonNilObservable(form.value$);
  const listItems = useObservable(form.controls.items.value$, []);
  const [columnAItems, setColumnAItems] = useState<MatchingListItem[]>([]);
  const [columnBItems, setColumnBItems] = useState<MatchingListItem[]>([]);
  const [isMatchingCompleted, setIsMatchingCompleted] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState(false);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    setColumnAItems(R.shuffle(listItems.map(item => ({ ...item, id: R.uniqueId() }))));
    setColumnBItems(R.shuffle(listItems.map(item => ({ ...item, id: R.uniqueId() }))));
  }, [listItems]);

  useConfigureBlock({
    props,
    form,
    contentComponent: ListMatchingBlockSettings,
    contentProps: { form, variation },
  });

  const onFirstSortEnd = useCallback((itemsNew: any[]) => {
    setColumnAItems(itemsNew);
  }, []);

  const onSecondSortEnd = useCallback((itemsNew: any[]) => {
    setColumnBItems(itemsNew);
  }, []);

  const onResetMatching = useCallback(() => {
    setIsMatchingCompleted(false);
    setWrongAnswer(false);
    setColumnAItems(R.shuffle(listItems.map(item => ({ ...item, id: R.uniqueId() }))));
    setColumnBItems(R.shuffle(listItems.map(item => ({ ...item, id: R.uniqueId() }))));
  }, [listItems]);

  const onNext = useCallback(() => {
    const result = columnAItems.length === columnBItems.length && columnAItems.every((obj, index) => obj.item === columnBItems[index].item);

    if (!props.editable) {
      bloc
        .onProgress(lessonId || '', {
          title: form.controls.instructions.value,
          answer: [JSON.stringify(columnAItems) + JSON.stringify(columnBItems)],
          correct: JSON.stringify(listItems),
          status: result ? 'pass' : 'fail',
        })
        .subscribe();
    }
    if (result) {
      return setWrongAnswer(false), setIsMatchingCompleted(true);
    } else {
      return setWrongAnswer(true), setIsMatchingCompleted(false);
    }
  }, [columnAItems, columnBItems, props.editable, bloc, lessonId, form.controls.instructions.value, listItems]);

  const onSubmit = useCallback(() => of(isMatchingCompleted && !wrongAnswer), [wrongAnswer, isMatchingCompleted]);

  useCourseForm(props.id, form, { onSubmit });

  const onHeight = useCallback((height: number) => {
    setHeight(h => (height > h ? height : h));
  }, []);

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-list', 'mt-3', variation)} advanceForm={formValue.advance}>
        <div className="ml-4 mt-4 noselect">
          <p>{HTMLService.toReact(form.controls.instructions.value || '')}</p>
          {!isMatchingCompleted && (
            <Row>
              <Col md={6}>
                <Sortable
                  lockAxis="y"
                  items={columnAItems}
                  getId={c => c.id}
                  onSort={onFirstSortEnd}
                  renderItem={item => <ListItem height={height > 0 ? height : undefined} onHeightChange={onHeight} item={item.item} />}
                />
              </Col>
              <Col md={6}>
                <Sortable
                  lockAxis="y"
                  items={columnBItems}
                  getId={c => c.id}
                  onSort={onSecondSortEnd}
                  renderItem={item => <ListItem height={height > 0 ? height : undefined} onHeightChange={onHeight} item={item.matchingItem} />}
                />
              </Col>
              <div className="d-flex align-items-start mt-2 ml-3 flex-column">
                <Button type="button" onClick={onNext}>
                  Next
                </Button>
              </div>
            </Row>
          )}
          {isMatchingCompleted && (
            <div className="d-flex align-items-start mt-2 flex-column">
              <span className="text-success mb-2">Congratulations! You have completed this drill successfully.</span>
              <Button type="button" onClick={onResetMatching}>
                Redo Drill
              </Button>
            </div>
          )}
          {wrongAnswer && (
            <div className="d-flex align-items-start mt-2 flex-column">
              <span className="text-danger mb-2">You have to get 100% correct answers to pass this drill.</span>
              <Button type="button" onClick={onResetMatching}>
                Redo Drill
              </Button>
            </div>
          )}
        </div>
      </BlockContainer>
    </Container>
  );
});

interface ListItemProps {
  item: string;
  height?: number;
  onHeightChange: (height: number) => void;
}

const ListItem = memo((props: ListItemProps) => {
  const { item, height, onHeightChange } = props;
  const divRef = useRef<HTMLDivElement>(null);
  const [orignalHeight, setHeight] = useState(0);

  useEffect(() => {
    setHeight(divRef.current.clientHeight);
    onHeightChange(divRef.current.clientHeight);
  }, [onHeightChange]);

  return (
    <div ref={divRef} className="row list-matching-row flex-fill" style={{ height }}>
      <Col md={12} className="leftCol">
        {item}
      </Col>
    </div>
  );
});
